(() => {
  const namespace = window.AuremeStaffstartDefaultOverrides || {};
  window.AuremeStaffstartDefaultOverrides = namespace;
  // URL 正規化は theme.liquid で先行読み込みする aureme-url.js に集約する。
  const auremeUrl = window.AuremeUrl;
  if (!auremeUrl) return;

  const ROOT_SELECTOR = '#staffstart-shopify-app-container';
  const STAFFSTART_ROOT_PATH = '/apps/staffstart/';
  const COORDINATE_INDEX_PATH = '/apps/staffstart/coordinates';
  const STAFF_DETAIL_PATH_PATTERN = /^\/apps\/staffstart\/staffs\/(\d+)$/;
  const COORDINATE_DETAIL_PATH_PATTERN = /^\/apps\/staffstart\/coordinates\/[^/]+\/?$/;
  const COORDINATE_DETAIL_BODY_CLASS = 'aureme-staffstart-coordinate-detail-page';
  const HISTORY_HOOK_KEY = '__auremeStaffstartDefaultOverridesHistoryHooked';
  const PATCH_ORDER = Object.freeze({
    common: Object.freeze({
      breadcrumb: 10,
    }),
    coordinateIndex: Object.freeze({
      brandFilter: 20,
      filterAllOption: 30,
      typeFilter: 40,
      filterToggle: 50,
      textReplacement: 60,
      typography: 70,
      staffName: 80,
    }),
    staffDetail: Object.freeze({
      socialLinks: 90,
      typography: 100,
      coordinateTab: 110,
    }),
    coordinateDetail: Object.freeze({
      typography: 120,
      swiperGeometry: 130,
    }),
  });

  /**
   * @template T
   * @typedef {object} StaffstartPatch
   * @property {number} order - patch 実行順
   *   旧 patch 配列順と同じ 10 刻みで、patchOrder の値を割り当てる。
   * @property {() => boolean} isTargetRoute - marker を有効にする route 判定
   * @property {(root: HTMLElement) => T} collect - marker 対象の収集処理
   * @property {() => T} createEmptyTargets - route 対象外で空同期するための初期値
   * @property {(root: HTMLElement, elements: T) => void} apply - marker の反映処理
   */

  /**
   * @typedef {StaffstartPatch<unknown>} StaffstartRegisteredPatch
   */

  /**
   * 共有 Set を汚染しないよう、空の marker 対象を都度返す。
   * @returns {Set<HTMLElement>}
   */
  function createEmptyMarkedElements() {
    return new Set();
  }

  /**
   * root まで親方向へ辿り、条件に一致する最初の要素を返す。
   * @param {HTMLElement | null} start - 探索開始要素
   * @param {HTMLElement} root - 探索境界
   * @param {(element: HTMLElement) => boolean} predicate - 対象要素の判定
   * @param {number} [maxDepth] - 最大探索階層数
   * @returns {HTMLElement | null}
   */
  function findAncestorUntilRoot(start, root, predicate, maxDepth) {
    let current = start;
    let depth = 0;
    const depthLimit = typeof maxDepth === 'number' ? maxDepth : Number.POSITIVE_INFINITY;

    while (current && current !== root && depth < depthLimit) {
      if (predicate(current)) return current;

      current = current.parentElement;
      depth += 1;
    }

    return null;
  }

  /**
   * locale prefix を除いた route 判定用 path を返す。
   * @param {string} pathname - 判定対象 path
   * @returns {string}
   */
  function getRoutePathname(pathname) {
    const normalizedPathname = String(pathname || '');
    const rootUrl = auremeUrl.normalizeRootUrl(namespace.rootUrl);
    if (rootUrl && normalizedPathname.startsWith(`${rootUrl}/`)) {
      return normalizedPathname.slice(rootUrl.length) || '/';
    }

    return normalizedPathname;
  }

  /**
   * route 判定用に末尾スラッシュだけを正規化する。
   * @param {string} pathname - 判定対象 path
   * @returns {string}
   */
  function normalizePathname(pathname) {
    const routePathname = getRoutePathname(pathname);
    if (routePathname === STAFFSTART_ROOT_PATH) return routePathname;

    return routePathname.replace(/\/+$/, '');
  }

  /**
   * STAFF START アプリ配下の画面かどうかを返す。
   * @returns {boolean}
   */
  function isStaffstartPath() {
    return getRoutePathname(window.location.pathname).startsWith(STAFFSTART_ROOT_PATH);
  }

  /**
   * STAFF SNAP 一覧画面かどうかを返す。
   * @returns {boolean}
   */
  function isCoordinateIndexPath() {
    return normalizePathname(window.location.pathname) === COORDINATE_INDEX_PATH;
  }

  /**
   * STAFF START スタッフ詳細画面の staff ID を返す。
   * @returns {string}
   */
  function getStaffDetailId() {
    return normalizePathname(window.location.pathname).match(STAFF_DETAIL_PATH_PATTERN)?.[1] || '';
  }

  /**
   * STAFF START スタッフ詳細画面かどうかを返す。
   * @returns {boolean}
   */
  function isStaffDetailPath() {
    return Boolean(getStaffDetailId());
  }

  /**
   * スタッフ詳細のプロフィール行かどうかを返す。
   * @param {Element | null} element - 判定対象要素
   * @returns {boolean}
   */
  function isStaffProfileSummaryRow(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.children.length < 2) return false;

    const profileContent = element.firstElementChild;
    if (!(profileContent instanceof HTMLElement)) return false;

    // スタッフ詳細の先頭行は、左側プロフィール領域にスタッフ名 h2 を含む。
    return Boolean(profileContent.querySelector('h2'));
  }

  /**
   * スタッフ詳細のプロフィール先頭行を返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {HTMLElement | null}
   */
  function findStaffProfileSummaryRow(root) {
    const profileSection = root.querySelector('section');
    const summaryRow = profileSection?.firstElementChild;

    return isStaffProfileSummaryRow(summaryRow) ? summaryRow : null;
  }

  /**
   * STAFF START スタッフ詳細画面の route path を返す。
   * @returns {string}
   */
  function getStaffDetailPath() {
    const staffId = getStaffDetailId();

    return staffId ? `/apps/staffstart/staffs/${staffId}` : '';
  }

  /**
   * STAFF SNAP 詳細画面かどうかを返す。
   * @returns {boolean}
   */
  function isCoordinateDetailPath() {
    // Liquid 側の初回描画判定と揃えるため、末尾 slash 1 つの許容は正規表現側で扱う。
    return COORDINATE_DETAIL_PATH_PATTERN.test(getRoutePathname(window.location.pathname));
  }

  /**
   * STAFF SNAP 詳細画面用の body class を route に合わせて同期する。
   * route 変更直後と補正実行直前の両方で呼び、body class の残留を防ぐ。
   */
  function syncCoordinateDetailBodyClass() {
    document.body?.classList.toggle(COORDINATE_DETAIL_BODY_CLASS, isCoordinateDetailPath());
  }

  /**
   * 要素の前後空白と連続空白を揃えた文字列を返す。
   * @param {Element | null} element - 対象要素
   * @returns {string}
   */
  function getTrimmedText(element) {
    return (element?.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * element が reference より DOM 順で前にあるかどうかを返す。
   * @param {Element} element - 判定対象要素
   * @param {Element} reference - 基準要素
   * @returns {boolean}
   */
  function isBeforeElement(element, reference) {
    // reference が element の後ろにある場合、element は DOM 順で前にある。
    return Boolean(element.compareDocumentPosition(reference) & Node.DOCUMENT_POSITION_FOLLOWING);
  }

  /**
   * 既存 marker と今回検出した要素を同期する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {string} attributeName - 同期対象属性
   * @param {Set<HTMLElement>} elements - marker を付与する要素
   */
  function syncAttributeMarkers(root, attributeName, elements) {
    for (const markedElement of root.querySelectorAll(`[${attributeName}]`)) {
      if (!(markedElement instanceof HTMLElement)) continue;
      if (elements.has(markedElement)) continue;

      markedElement.removeAttribute(attributeName);
    }

    for (const element of elements) {
      if (element.hasAttribute(attributeName)) continue;

      element.setAttribute(attributeName, '');
    }
  }

  /**
   * STAFF START 補正の generic runtime を返す。
   * @returns {{registerPatch: (patch: StaffstartRegisteredPatch) => void, start: () => void}}
   */
  function createStaffstartOverridesRuntime() {
    /** @type {Array<StaffstartRegisteredPatch>} */
    const patches = [];
    let observer = null;
    let patchFrameId = 0;
    let isStarted = false;

    /**
     * patch の route 判定に応じて marker 対象を返す。
     * @template T
     * @param {HTMLElement} root - STAFF START アプリの描画ルート
     * @param {StaffstartPatch<T>} patch - 反映対象 patch
     * @returns {T}
     */
    function collectPatchTargets(root, patch) {
      // route 対象外では marker を空同期し、soft navigation 後の残留を防ぐ。
      return patch.isTargetRoute() ? patch.collect(root) : patch.createEmptyTargets();
    }

    /**
     * STAFF START アプリの描画後補正をまとめて反映する。
     */
    function patchStaffstartOverrides() {
      syncCoordinateDetailBodyClass();

      const root = document.querySelector(ROOT_SELECTOR);
      if (!(root instanceof HTMLElement)) return;

      for (const patch of patches) {
        patch.apply(root, collectPatchTargets(root, patch));
      }
    }

    /**
     * 外部 app の非同期描画に合わせて補正を次フレームへまとめる。
     */
    function schedulePatch() {
      if (patchFrameId) return;

      patchFrameId = window.requestAnimationFrame(() => {
        patchFrameId = 0;
        patchStaffstartOverrides();
      });
    }

    /**
     * STAFF START から離れた後に未実行の補正が走らないようにする。
     */
    function cancelScheduledPatch() {
      if (!patchFrameId) return;

      window.cancelAnimationFrame(patchFrameId);
      patchFrameId = 0;
    }

    /**
     * STAFF START 配下でだけ非同期描画の監視を有効化する。
     */
    function reconcileObserver() {
      syncCoordinateDetailBodyClass();

      if (!isStaffstartPath()) {
        observer?.disconnect();
        observer = null;
        cancelScheduledPatch();
        patchStaffstartOverrides();
        return;
      }

      if (!observer) {
        observer = new MutationObserver(schedulePatch);
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      }

      schedulePatch();
    }

    /**
     * app 内の soft navigation 後にも最小補正を再判定する。
     */
    function installHistoryHooks() {
      if (window[HISTORY_HOOK_KEY]) return;

      window[HISTORY_HOOK_KEY] = true;
      const { pushState, replaceState } = window.history;

      window.history.pushState = function pushStateWithStaffstartDefaultOverrides(...args) {
        const result = pushState.apply(this, args);
        reconcileObserver();
        return result;
      };

      window.history.replaceState = function replaceStateWithStaffstartDefaultOverrides(...args) {
        const result = replaceState.apply(this, args);
        reconcileObserver();
        return result;
      };

      window.addEventListener('popstate', reconcileObserver);
      window.addEventListener('pageshow', reconcileObserver);
    }

    return {
      registerPatch(patch) {
        patches.push(patch);
        patches.sort((a, b) => a.order - b.order);
        if (isStarted) schedulePatch();
      },
      start() {
        isStarted = true;
        installHistoryHooks();
        reconcileObserver();
      },
    };
  }

  const runtime = createStaffstartOverridesRuntime();
  namespace.runtime = {
    createEmptyMarkedElements,
    findAncestorUntilRoot,
    findStaffProfileSummaryRow,
    getStaffDetailId,
    getStaffDetailPath,
    getTrimmedText,
    isBeforeElement,
    isCoordinateDetailPath,
    isCoordinateIndexPath,
    isStaffDetailPath,
    isStaffstartPath,
    normalizePathname,
    patchOrder: PATCH_ORDER,
    registerPatch: runtime.registerPatch,
    syncAttributeMarkers,
  };
  runtime.start();
})();
